import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Spinner, Alert, Row, Col, Form, InputGroup, Badge, Collapse } from 'react-bootstrap';
import { Dish, Tag } from '../types/Dish';
import { fetchApi } from '../api';
import Pagination from './Pagination';
import ErrorDisplay from './ErrorDisplay';
import { useTranslation } from 'react-i18next';

const IS_PUBLIC_APP = process.env.REACT_APP_IS_PUBLIC_APP === 'true';
const DEFAULT_ITEMS_PER_PAGE = 100;

const parseItemsPerPage = (value: string | null): number => {
  const parsedValue = Number(value);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : DEFAULT_ITEMS_PER_PAGE;
};

const DishList: React.FC = () => {
  console.log('Public app mode:', IS_PUBLIC_APP);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [error, setError] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const searchTerm = searchParams.get('search') || '';
  const itemsPerPage = parseItemsPerPage(searchParams.get('itemsPerPage'));
  const selectedTags = React.useMemo(() => searchParams.getAll('tags[]'), [searchParams]);
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [isChoosingRandomDish, setIsChoosingRandomDish] = useState(false);
  const { t } = useTranslation();

  const fetchAvailableTags = async () => {
    try {
      const data = await fetchApi('/api/tags');
      setAvailableTags(data['hydra:member'] || data['member'] || []);
    } catch (err) {
      console.error('Failed to fetch tags', err);
    }
  };

  const fetchDishes = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        itemsPerPage: String(itemsPerPage),
      });

      if (searchTerm) {
        params.set('search[name]', searchTerm);
      }
      if (selectedTags.length > 0) {
        selectedTags.forEach((tagIri) => {
          params.append('tags[]', tagIri);
        });
      }
      const data = await fetchApi(`/api/dishes?${params}`, {
        preload: '/api/dish_ingredients/*',
      });
      const fetchedDishes = data['hydra:member'] || data['member'] || [];
      setDishes(fetchedDishes);
      setTotalItems(data['hydra:totalItems'] || data['totalItems'] || 0);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, page, searchTerm, selectedTags]);

  useEffect(() => {
    fetchAvailableTags();
  }, []);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    fetchDishes();
  }, [fetchDishes]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const nextParams = new URLSearchParams();
    if (searchInput) nextParams.set('search', searchInput);
    if (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) nextParams.set('itemsPerPage', String(itemsPerPage));
    selectedTags.forEach((tag) => nextParams.append('tags[]', tag));

    setSearchParams(nextParams);
    setPage(1);
  };

  const handleTagToggle = (tagIri: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('page'); // Reset page when filtering

    const currentTags = nextParams.getAll('tags[]');
    if (currentTags.includes(tagIri)) {
      // Remove tag
      const remainingTags = currentTags.filter((t) => t !== tagIri);
      nextParams.delete('tags[]');
      remainingTags.forEach((t) => nextParams.append('tags[]', t));
    } else {
      // Add tag
      nextParams.append('tags[]', tagIri);
    }

    setSearchParams(nextParams);
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchParams({});
    setPage(1);
  };

  const handleRandomDish = async () => {
    if (loading || isChoosingRandomDish || totalItems === 0) return;

    try {
      setIsChoosingRandomDish(true);
      const randomPage = Math.floor(Math.random() * totalItems) + 1;
      const params = new URLSearchParams({
        page: String(randomPage),
        itemsPerPage: '1',
      });

      if (searchTerm) {
        params.set('search[name]', searchTerm);
      }
      selectedTags.forEach((tagIri) => {
        params.append('tags[]', tagIri);
      });

      const data = await fetchApi(`/api/dishes?${params}`);
      const [dish] = data['hydra:member'] || data['member'] || [];
      const dishId = dish?.id || dish?.['@id']?.split('/').pop();

      if (dishId) {
        navigate(`/dishes/${dishId}`);
      } else {
        await fetchDishes();
      }
    } catch (err: any) {
      setError(err);
    } finally {
      setIsChoosingRandomDish(false);
    }
  };

  if (loading && dishes.length === 0) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('Loading dishes')}…</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 d-none d-lg-block d-xl-block d-xxl-block">
        <h1>{t('Dishes')}</h1>
      </div>

      <div className="mb-4">
        <Form onSubmit={handleSearch}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder={t('Search dishes by name')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button variant="light" type="submit">
              {t('Search')}
            </Button>
            <Button variant="secondary" type="button" onClick={handleReset}>
              {t('Reset')}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleRandomDish}
              disabled={loading || isChoosingRandomDish || totalItems === 0}
            >
              {isChoosingRandomDish && (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
              )}
              {isChoosingRandomDish ? t('Choosing random dish') : t('Random dish')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsTagsOpen(!isTagsOpen)}
              aria-controls="tags-collapse"
              aria-expanded={isTagsOpen}
              className="d-none d-lg-block d-xl-block d-xxl-block"
            >
              {isTagsOpen ? t('Hide tags') : t('Show tags')}
            </Button>
          </InputGroup>
        </Form>
      </div>

      <div className="mb-4">
        <Button
          onClick={() => setIsTagsOpen(!isTagsOpen)}
          aria-controls="tags-collapse"
          aria-expanded={isTagsOpen}
          className="d-block d-lg-none d-xxl-none mb-2"
        >
          {isTagsOpen ? t('Hide tags') : t('Show tags')}
        </Button>
        <Collapse in={isTagsOpen}>
          <div id="tags-collapse">
            <div className="d-flex flex-wrap gap-2 bg-white p-2">
              {availableTags.map((tag) => {
                const tagIri = tag['@id'] || `/api/tags/${tag.id}`;
                const isActive = selectedTags.includes(tagIri);
                return (
                  <Button
                    key={tagIri}
                    variant={isActive ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => handleTagToggle(tagIri)}
                  >
                    {tag.name}
                  </Button>
                );
              })}
            </div>
          </div>
        </Collapse>
      </div>

      {dishes.length === 0 ? (
        <Alert variant="info">{t('No dishes found.')}</Alert>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {dishes.map((dish) => (
            <Col key={dish.id || dish['@id']}>
              <Card h-100 className="bg-white shadow h-100">
                <Link to={`/dishes/${dish.id || dish['@id']?.split('/').pop()}`} className="text-decoration-none">
                  <Card.Body>
                    <Card.Title>{dish.name}</Card.Title>
                    <Card.Text className="text-muted">
                      {dish.description && dish.description.length > 100
                        ? `${dish.description.substring(0, 100)}...`
                        : dish.description}
                    </Card.Text>
                    {dish.tags && dish.tags.length > 0 && (
                      <div className="mt-0">
                        {(dish.tags as Tag[]).map((tag, idx) => (
                          <Badge key={idx} bg="primary" pill={false} className="me-1" style={{ fontSize: '0.7rem' }}>
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Link>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Pagination currentPage={page} totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={setPage} />
    </div>
  );
};

export default DishList;
